/** biome-ignore-all lint/complexity/noStaticOnlyClass: Stateless API wrapper, static methods are sufficient. */
import { apiClient } from '../../ApiClient';
import type { IPaginatedResponse, UtilRequestConfig } from '../../types';
import type {
	TBilling,
	TBillingCheckoutUrlData,
	TBillingCreateCustomerSubscriptionData,
	TBillingCreateCustomerSubscriptionResponse,
	TBillingPayment,
	TBillingPaymentMethodsResponse,
	TBillingPaymentsParams,
	TBillingPayOverageData,
	TBillingPayUsageDebtData,
	TBillingProduct,
	TBillingProductsParams,
	TBillingStripeUrlData,
	TBillingTransaction,
	TBillingTransactionsParams,
	TBillingUnitsUsageParams,
	TBillingUnitsUsageResponse,
	TBillingUsagePaymentResponse,
	TCreateBillingPaymentMethodSetupResponse,
	TCreateProductData,
	TProduct,
	TUpdateProductData
} from './types';

const PRODUCTS_ENDPOINT = 'v1/billing/products';
const PAYMENT_METHODS_ENDPOINT = 'v1/billing/payment-methods';

const getProductEndpoint = (productId: string): string =>
	`${PRODUCTS_ENDPOINT}/${encodeURIComponent(productId)}`;

const getPaymentMethodEndpoint = (paymentMethodId: string): string =>
	`${PAYMENT_METHODS_ENDPOINT}/${encodeURIComponent(paymentMethodId)}`;

export class BillingService {
	static getBilling(config?: UtilRequestConfig): Promise<TBilling> {
		return apiClient.get<TBilling>('v1/customer/billing', config);
	}

	static getBillingUnitsUsage(
		params: TBillingUnitsUsageParams,
		config?: UtilRequestConfig
	): Promise<TBillingUnitsUsageResponse> {
		return apiClient.get<TBillingUnitsUsageResponse>('v2/bff/billing/usage', {
			...config,
			params
		});
	}

	static getBillingStripeUrl(
		data: TBillingStripeUrlData,
		config?: UtilRequestConfig
	): Promise<{ url: string }> {
		return apiClient.post<{ url: string }>(
			'v1/customer/billing/get-stripe-url',
			data,
			config
		);
	}

	static getBillingCheckoutUrl(
		data: TBillingCheckoutUrlData,
		config?: UtilRequestConfig
	): Promise<{ url: string }> {
		return apiClient.post<{ url: string }>(
			'v1/customer/billing/generate-checkout-url',
			data,
			config
		);
	}

	static createCustomerSubscription(
		data: TBillingCreateCustomerSubscriptionData,
		config?: UtilRequestConfig
	): Promise<TBillingCreateCustomerSubscriptionResponse> {
		return apiClient.post<TBillingCreateCustomerSubscriptionResponse>(
			'v1/customer/billing/create-subscription',
			data,
			config
		);
	}

	static payUsageDebt(
		data: TBillingPayUsageDebtData,
		config?: UtilRequestConfig
	): Promise<TBillingUsagePaymentResponse> {
		return apiClient.post<TBillingUsagePaymentResponse>(
			'v1/customer/billing/pay-usage-debt',
			data,
			config
		);
	}

	static payOverage(
		data: TBillingPayOverageData,
		config?: UtilRequestConfig
	): Promise<TBillingUsagePaymentResponse> {
		return apiClient.post<TBillingUsagePaymentResponse>(
			'v1/customer/billing/pay-overage',
			data,
			config
		);
	}

	static completeDevelopmentStage(config?: UtilRequestConfig): Promise<void> {
		return apiClient.post<void>(
			'v1/customer/billing/complete-development-stage',
			undefined,
			config
		);
	}

	static getBillingTransactions(
		params: TBillingTransactionsParams,
		config?: UtilRequestConfig
	): Promise<IPaginatedResponse<TBillingTransaction>> {
		return apiClient.get<IPaginatedResponse<TBillingTransaction>>(
			'v1/customer/billing/journal-entries',
			{ ...config, params }
		);
	}

	static getBillingPayments(
		params?: TBillingPaymentsParams,
		config?: UtilRequestConfig
	): Promise<TBillingPayment[]> {
		return apiClient.get<TBillingPayment[]>('v1/customer/billing/payments', {
			...config,
			params
		});
	}

	static getPaymentMethods(
		config?: UtilRequestConfig
	): Promise<TBillingPaymentMethodsResponse> {
		return apiClient
			.get<TBillingPaymentMethodsResponse>(PAYMENT_METHODS_ENDPOINT, config)
			.then((response) => ({
				...response,
				items: response.items.map((paymentMethod) => ({
					...paymentMethod,
					is_default: paymentMethod.id === response.default_payment_method_id
				}))
			}));
	}

	static async createPaymentMethodSetup(
		config?: UtilRequestConfig
	): Promise<string> {
		const { client_secret } =
			await apiClient.post<TCreateBillingPaymentMethodSetupResponse>(
				`${PAYMENT_METHODS_ENDPOINT}/setup-intent`,
				undefined,
				config
			);

		if (!client_secret) {
			throw new Error('Missing payment method setup client secret');
		}

		return client_secret;
	}

	static setDefaultPaymentMethod(
		paymentMethodId: string,
		config?: UtilRequestConfig
	): Promise<void> {
		return apiClient.post<void>(
			`${getPaymentMethodEndpoint(paymentMethodId)}/set-default`,
			undefined,
			config
		);
	}

	static deletePaymentMethod(
		paymentMethodId: string,
		config?: UtilRequestConfig
	): Promise<void> {
		return apiClient.delete<void>(
			getPaymentMethodEndpoint(paymentMethodId),
			config
		);
	}

	static getBillingProducts(
		params?: TBillingProductsParams,
		config?: UtilRequestConfig
	): Promise<TBillingProduct[]> {
		return apiClient.get<TBillingProduct[]>('v1/customer/billing/products', {
			...config,
			params
		});
	}

	static getProducts(config?: UtilRequestConfig): Promise<TProduct[]> {
		return apiClient.get<TProduct[]>(PRODUCTS_ENDPOINT, config);
	}

	static createProduct(
		data: TCreateProductData,
		config?: UtilRequestConfig
	): Promise<TProduct> {
		return apiClient.post<TProduct>(PRODUCTS_ENDPOINT, data, config);
	}

	static updateProduct(
		productId: string,
		data: TUpdateProductData,
		config?: UtilRequestConfig
	): Promise<TProduct> {
		return apiClient.patch<TProduct>(
			getProductEndpoint(productId),
			data,
			config
		);
	}

	static deleteProduct(
		productId: string,
		config?: UtilRequestConfig
	): Promise<void> {
		return apiClient.delete<void>(getProductEndpoint(productId), config);
	}
}
